import { useClientContext } from '@/contexts/ClientContext';
import { useAuth } from '@/hooks/useAuth';
import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook para garantir isolamento de dados por cliente
 * Retorna o client_id que deve ser usado em todas as queries
 * e funções utilitárias para validação de acesso
 * 
 * IMPORTANTE: Este hook usa o ClientContext que implementa
 * invalidação automática de cache quando o cliente muda.
 */
export const useClientFilter = () => {
  const { 
    currentClientId: contextClientId, 
    loading: clientLoading,
    availableClients,
    canSwitchClients,
    showAllClients,
    switchClient,
    setShowAllClients,
    invalidateClientQueries
  } = useClientContext();
  const { profile, user } = useAuth();
  const [currentClientId, setCurrentClientId] = useState<string | null>(null);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [userClients, setUserClients] = useState<string[]>([]);

  // Buscar o client_id e client_ids diretamente do banco quando necessário
  useEffect(() => {
    const fetchClientId = async () => {
      if (!user?.id) {
        setCurrentClientId(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('users')
          .select('client_id, client_ids')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching user client data:', error);
          setCurrentClientId(null);
          return;
        }

        // Priorizar client_id se existir, senão usar primeiro de client_ids
        let primaryClientId = data?.client_id || null;
        
        if (!primaryClientId && data?.client_ids) {
          const clientIds = Array.isArray(data.client_ids) ? data.client_ids : 
            (typeof data.client_ids === 'string' ? JSON.parse(data.client_ids) : []);
          primaryClientId = clientIds.length > 0 ? clientIds[0] : null;
        }

        setCurrentClientId(primaryClientId);
        
        // Para técnicos, armazenar todos os client_ids
        if (profile?.role === 'technician') {
          const clientIds = Array.isArray(data?.client_ids) ? data.client_ids : 
            (typeof data?.client_ids === 'string' ? JSON.parse(data.client_ids) : []);
          const finalClientIds = clientIds.length > 0 ? clientIds : (primaryClientId ? [primaryClientId] : []);
          setUserClients(finalClientIds);
          
          // Buscar grupos do técnico
          const { data: groupsData, error: groupsError } = await supabase
            .from('user_groups')
            .select('group_id')
            .eq('user_id', user.id);

          if (!groupsError && groupsData) {
            const groupIds = groupsData.map(ug => ug.group_id);
            setUserGroups(groupIds);
          } else {
            setUserGroups([]);
          }
        }
      } catch (err) {
        console.error('Error in fetchClientId:', err);
        setCurrentClientId(null);
      }
    };

    // Para técnicos, sempre buscar dados completos, ignorando o contexto
    // porque técnicos podem ter múltiplos clientes
    if (profile?.role === 'technician' && user?.id) {
      fetchClientId();
    } else if (contextClientId) {
      // Para outros usuários, usar o valor do contexto se disponível
      setCurrentClientId(contextClientId);
    } else if (user?.id) {
      // Senão buscar diretamente
      fetchClientId();
    }
  }, [contextClientId, user?.id, profile?.role]);

  // Verificar se o usuário é admin (pode ver todos os clientes)
  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
  
  // Verificar se o usuário é admin do cliente (admin limitado ao seu cliente)
  const isClientAdmin = profile?.role === 'client_admin';
  
  // Verificar se o usuário é técnico
  const isTechnician = profile?.role === 'technician';

  /**
   * Retorna o filtro que deve ser aplicado nas queries
   * Para admins com showAllClients: null (sem filtro)
   * Para admins com cliente selecionado: client_id específico
   * Para client_admins e usuários normais: client_id obrigatório
   */
  const getClientFilter = () => {
    // Se está no modo "Todos os Clientes" e pode alternar
    if (showAllClients && canSwitchClients) {
      return null; // Sem filtro - mostra todos
    }
    
    if (isAdmin && !contextClientId && !showAllClients) {
      return null; // Admin sem cliente selecionado vê tudo
    }
    
    // Se há um cliente específico selecionado no contexto, usar ele
    if (contextClientId) {
      return contextClientId;
    }
    
    return currentClientId; // Client_admin e usuários normais veem apenas seu cliente
  };

  /**
   * Retorna filtros específicos para técnicos
   * Técnicos podem ver dados dos seus clientes e grupos
   */
  const getTechnicianFilter = () => {
    if (!isTechnician) return null;
    
    return {
      clientIds: userClients,
      groupIds: userGroups
    };
  };

  /**
   * Valida se um registro pode ser acessado pelo usuário atual
   * Agora considera tanto client_id quanto client_ids do usuário
   */
  const canAccessRecord = async (recordClientId: string | null) => {
    if (isAdmin) {
      return true; // Admin pode acessar qualquer registro
    }
    
    if (!user?.id || !recordClientId) {
      return false; // Usuário sem ID ou registro sem cliente
    }

    try {
      // Buscar dados completos do usuário para verificar acesso
      const { data, error } = await supabase
        .from('users')
        .select('client_id, client_ids')
        .eq('id', user.id)
        .single();

      if (error || !data) {
        return false;
      }

      // Verificar client_id único
      if (data.client_id === recordClientId) {
        return true;
      }

      // Verificar client_ids múltiplos
      if (data.client_ids) {
        const clientIds = Array.isArray(data.client_ids) ? data.client_ids : 
          (typeof data.client_ids === 'string' ? JSON.parse(data.client_ids) : []);
        return clientIds.includes(recordClientId);
      }

      return false;
    } catch (err) {
      console.error('Error checking record access:', err);
      return false;
    }
  };

  /**
   * Aplica filtro de cliente em uma query do Supabase
   * Agora considera showAllClients
   */
  const applyClientFilter = (query: any, clientIdField: string = 'client_id') => {
    const clientFilter = getClientFilter();
    
    if (clientFilter) {
      return query.eq(clientIdField, clientFilter);
    }
    
    return query; // Admin/showAllClients ou sem filtro
  };

  /**
   * Valida se dados podem ser inseridos/atualizados
   */
  const validateClientData = (data: any) => {
    if (isAdmin) {
      return { isValid: true, data };
    }

    // Técnicos podem ter acesso a múltiplos clientes. Respeitar o client_id
    // vindo do formulário (ex.: definido pela categoria selecionada) desde que
    // o técnico tenha acesso a esse cliente.
    if (isTechnician) {
      const requestedClientId = data?.client_id;
      if (requestedClientId) {
        const allowed =
          userClients.includes(requestedClientId) ||
          requestedClientId === currentClientId;
        if (!allowed) {
          return {
            isValid: false,
            error: 'Você não possui acesso ao cliente selecionado',
          };
        }
        return { isValid: true, data };
      }
      // Sem client_id no form -> usar o cliente atual como fallback
      if (!currentClientId) {
        return { isValid: false, error: 'Usuário não possui cliente associado' };
      }
      return { isValid: true, data: { ...data, client_id: currentClientId } };
    }

    if (!currentClientId) {
      return {
        isValid: false,
        error: 'Usuário não possui cliente associado'
      };
    }

    // Para client_admin e usuários normais, garantir que o client_id está definido
    const validatedData = {
      ...data,
      client_id: currentClientId
    };

    return { isValid: true, data: validatedData };
  };

  /**
   * Middleware para queries que precisam de isolamento
   */
  const withClientIsolation = async (queryFn: (clientId: string | null) => Promise<any>) => {
    if (clientLoading) {
      throw new Error('Cliente ainda carregando');
    }

    const clientFilter = getClientFilter();
    return await queryFn(clientFilter);
  };

  /**
   * Verifica se o usuário atual pode gerenciar usuários
   * Admin global e client_admin podem gerenciar usuários
   */
  const canManageUsers = () => {
    return isAdmin || isClientAdmin;
  };

  /**
   * Verifica se o usuário atual pode criar/editar uma role específica
   * Admin global pode criar qualquer role
   * Client_admin não pode criar admin ou super_admin
   */
  const canAssignRole = (targetRole: string) => {
    if (isAdmin) {
      return true; // Admin pode atribuir qualquer role
    }
    
    if (isClientAdmin) {
      // Client_admin não pode criar admin ou super_admin
      return !['admin', 'super_admin'].includes(targetRole);
    }
    
    return false;
  };

  /**
   * Verifica se o usuário atual pode gerenciar um usuário específico
   * Baseado no client_id do usuário alvo
   */
  const canManageUser = (targetUserClientId: string | null, targetUserRole: string) => {
    if (isAdmin) {
      return true; // Admin pode gerenciar qualquer usuário
    }
    
    if (isClientAdmin) {
      // Client_admin não pode gerenciar admin ou super_admin
      if (['admin', 'super_admin'].includes(targetUserRole)) {
        return false;
      }
      // Client_admin só pode gerenciar usuários do seu tenant
      return targetUserClientId === currentClientId;
    }
    
    return false;
  };

  return {
    currentClientId: showAllClients ? null : (contextClientId || currentClientId),
    isAdmin,
    isClientAdmin,
    isTechnician,
    clientLoading,
    userGroups,
    userClients,
    availableClients,
    canSwitchClients,
    showAllClients,
    switchClient,
    setShowAllClients,
    invalidateClientQueries,
    getClientFilter,
    getTechnicianFilter,
    canAccessRecord,
    applyClientFilter,
    validateClientData,
    withClientIsolation,
    canManageUsers,
    canAssignRole,
    canManageUser
  };
};
